import styled from "@emotion/styled"
import Typography from "@mui/material/Typography"
import { Link } from "utils"
import { richTextListStyles } from "../../common/richTextListStyles"
import { typographyVariants } from "../../theme/typography"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

export const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[16]}px;
  padding: ${sizes.spacing[48]}px 0;

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${sizes.spacing[64]}px 0;
  }
`

export const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${sizes.spacing[6]}px;
  width: 100%;
`

/* The chevron boxes to one title line so it hugs the first line when the
   rich-text title wraps (same pattern as VehicleFleetGallery, EGDE-476). */
export const ChevronWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: ${typographyVariants.h2.mobile.lineHeight}px;

  @media (min-width: ${breakpoints.lg}px) {
    height: ${typographyVariants.h2.desktop.lineHeight}px;

    & svg {
      width: 21px;
      height: 36px;
    }
  }

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

export const Title = styled(Typography)`
  color: ${colors.light.text.primary};

  /* Rich-text title: block elements render at the wrapper's typography. */
  & p,
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin: 0;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }

  & a {
    color: ${colors.primitives.linkBlue} !important;
    text-decoration: underline;
  }

  ${richTextListStyles}
` as typeof Typography

export const Subtitle = styled(Typography)`
  color: ${colors.primitives.neutral600};
  text-align: start;
  width: 100%;
`

export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${sizes.spacing[16]}px;
  width: 100%;
  margin-top: ${sizes.spacing[24]}px;

  @media (min-width: ${breakpoints.lg}px) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${sizes.spacing[24]}px;
    margin-top: ${sizes.spacing[32]}px;
  }
`

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[8]}px;
  padding: ${sizes.spacing[24]}px;
  background: ${colors.primitives.neutral0};
  border: 1px solid ${colors.primitives.neutral200};
  border-radius: ${sizes.borderRadius[16]}px;
  box-shadow: 0px 2px 12px rgba(38, 48, 61, 0.06);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: ${colors.shadows.button};
  }
`

/* EGDE-549: a fixed square box keeps every icon the same footprint no matter
   the uploaded asset's intrinsic size (Figma renders the card icon at 60×60). */
export const CardIcon = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: ${sizes.borderRadius[12]}px;
  margin-bottom: ${sizes.spacing[8]}px;
`

export const CardTitle = styled(Typography)`
  color: ${colors.light.text.primary};
  font-weight: 700 !important;
  text-align: start;
  /* Figma Body 1 Bold (20px) on every viewport — the responsive body1 pair
     would drop the card title to 18px on mobile. */
  font-size: ${typographyVariants.body1.desktop.fontSize}px !important;
  line-height: ${typographyVariants.body1.desktop.lineHeight}px !important;
`

export const CardText = styled(Typography)`
  color: ${colors.primitives.neutral600};
  text-align: start;
  white-space: pre-line;
  /* Figma Body 2 Regular (18px) on every viewport. */
  font-size: ${typographyVariants.body2.desktop.fontSize}px !important;
  line-height: ${typographyVariants.body2.desktop.lineHeight}px !important;
`

/* Card link CTA — label + orange arrow, same interaction language as the
   footer subsidiary links (EGDE-507): the arrow nudges along its pointing
   direction on hover via logical margins (translateX is direction-flipped
   by stylis-rtl). */
export const CardCta = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  margin-top: auto;
  padding-top: ${sizes.spacing[8]}px;
  text-decoration: none;
  color: ${colors.light.text.primary};
  font-weight: 700;
  /* Figma Body 1 Bold (20px) on every viewport — inheriting the body font
     rendered the CTA at 18px on mobile. */
  font-size: ${typographyVariants.body1.desktop.fontSize}px;
  line-height: ${typographyVariants.body1.desktop.lineHeight}px;
  transition: color 0.2s;

  svg {
    color: ${colors.primitives.primary500};
    flex-shrink: 0;
    transition: margin 0.2s ease;
  }

  &:hover {
    color: ${colors.primitives.primary500};

    svg {
      margin-inline-start: ${sizes.spacing[4]}px;
      margin-inline-end: -${sizes.spacing[4]}px;
    }
  }

  [dir="ltr"] & {
    flex-direction: row-reverse;

    svg {
      /* @noflip */
      transform: scaleX(-1);
    }
  }
`
